import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Heading,
  HStack,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi";
import CodesetSelect from "../../CodesetSelect";
import UuidPreview from "../../UuidPreview";
import RateAmountInput from "../../RateAmountInput";
import CurrencyAmountInput from "../../CurrencyAmountInput";
import PartySelectById from "../../PartySelectById";
import { createEmptyCommission, createEmptyCommissionValue } from "../../utils/placementDefaults";

const CommissionValueEditor = ({ commissionValues = [], onChange }) => {
  const updateValue = (valueIndex, field, value) => {
    const updated = [...commissionValues];
    updated[valueIndex] = {
      ...updated[valueIndex],
      [field]: value,
    };
    onChange(updated);
  };

  const addValue = () => {
    onChange([...commissionValues, createEmptyCommissionValue()]);
  };

  const removeValue = (valueIndex) => {
    const updated = [...commissionValues];
    updated.splice(valueIndex, 1);
    onChange(updated);
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mb={4} bg="gray.50">
      <HStack mb={2}>
        <Heading size="xs">Commission Values</Heading>
        <IconButton
          icon={<HiOutlinePlus />}
          size="xs"
          aria-label="Add Commission Value"
          onClick={addValue}
        />
      </HStack>

      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>Seq#</Th>
            <Th>Basis</Th>
            <Th>Percentage</Th>
            <Th>Loss Ratio %</Th>
            <Th>Ceding Commission %</Th>
            <Th>Amount</Th>
            <Th></Th>
          </Tr>
        </Thead>
        <Tbody>
          {commissionValues.map((value, valueIndex) => (
            <Tr key={valueIndex}>
              <Td>
                <Input
                  size="sm"
                  type="number"
                  value={value.sequenceNbr}
                  onChange={(e) =>
                    updateValue(valueIndex, "sequenceNbr", parseInt(e.target.value) || 0)
                  }
                />
              </Td>
              <Td>
                <Input
                  size="sm"
                  value={value.basis || ""}
                  onChange={(e) => updateValue(valueIndex, "basis", e.target.value)}
                />
              </Td>
              <Td>
                <RateAmountInput
                  value={value.percentage}
                  onChange={(val) => updateValue(valueIndex, "percentage", val)}
                />
              </Td>
              <Td>
                <RateAmountInput
                  value={value.lossRatioPercentage}
                  onChange={(val) => updateValue(valueIndex, "lossRatioPercentage", val)}
                />
              </Td>
              <Td>
                <RateAmountInput
                  value={value.cedingCommissionPercentage}
                  onChange={(val) => updateValue(valueIndex, "cedingCommissionPercentage", val)}
                />
              </Td>
              <Td>
                <CurrencyAmountInput
                  value={value.amount}
                  onChange={(val) => updateValue(valueIndex, "amount", val)}
                />
              </Td>
              <Td>
                <IconButton
                  size="xs"
                  icon={<HiOutlineTrash />}
                  aria-label="Delete Value"
                  onClick={() => removeValue(valueIndex)}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const CommissionEditor = ({ commission, commissionIndex, onUpdate, onRemove }) => {
  const updateField = (field, value) => {
    onUpdate(commissionIndex, field, value);
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mb={6} borderColor="gray.500">
      <UuidPreview uuid={commission.id} />

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        <FormControl>
          <FormLabel>Description</FormLabel>
          <Input
            value={commission.description || ""}
            onChange={(e) => updateField("description", e.target.value)}
          />
        </FormControl>

        <CodesetSelect
          label="Type"
          codesetCode="A79"
          value={commission.type ? { code: commission.type, description: "" } : null}
          onChange={(value) => updateField("type", value.code)}
        />

        <CodesetSelect
          label="Basis"
          codesetCode="A80"
          value={commission.basis ? { code: commission.basis, description: "" } : null}
          onChange={(value) => updateField("basis", value.code)}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <CodesetSelect
          label="Calculation Type"
          codesetCode="A81"
          value={
            commission.calculationType
              ? { code: commission.calculationType, description: "" }
              : null
          }
          onChange={(value) => updateField("calculationType", value.code)}
        />

        <RateAmountInput
          label="Management Expenses %"
          value={commission.managementExpensesPercentage}
          onChange={(value) => updateField("managementExpensesPercentage", value)}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <PartySelectById
          label="Payable To Party"
          value={commission.payableToParty}
          onChange={(value) => updateField("payableToParty", value)}
        />

        <PartySelectById
          label="Payable By Party"
          value={commission.payableByParty}
          onChange={(value) => updateField("payableByParty", value)}
        />
      </SimpleGrid>

      <CommissionValueEditor
        commissionValues={commission.commissionValues || []}
        onChange={(values) => updateField("commissionValues", values)}
      />

      <HStack justify="flex-end" mt={4}>
        <IconButton
          size="md"
          icon={<HiOutlineTrash />}
          aria-label="Delete Commission"
          onClick={() => onRemove(commissionIndex)}
        />
      </HStack>
    </Box>
  );
};

const CommissionsEditor = ({ commissions = [], onChange }) => {
  const updateCommission = (commissionIndex, field, value) => {
    const updated = [...commissions];
    updated[commissionIndex] = {
      ...updated[commissionIndex],
      [field]: value,
    };
    onChange(updated);
  };

  const addCommission = () => {
    onChange([...commissions, createEmptyCommission()]);
  };

  const removeCommission = (commissionIndex) => {
    const updated = [...commissions];
    updated.splice(commissionIndex, 1);
    onChange(updated);
  };

  return (
    <Box>
      <HStack alignItems="center" mb={4}>
        <Heading size="md">Commissions</Heading>
        <IconButton
          icon={<HiOutlinePlus />}
          size="md"
          aria-label="Add Commission"
          onClick={addCommission}
        />
      </HStack>

      {commissions.map((commission, commissionIndex) => (
        <CommissionEditor
          key={commission.id}
          commission={commission}
          commissionIndex={commissionIndex}
          onUpdate={updateCommission}
          onRemove={removeCommission}
        />
      ))}
    </Box>
  );
};

export default CommissionsEditor;
